import { Button } from '@framework/ui/atoms/Button';
import { LoginForm } from '@user/Login';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFrown } from '@fortawesome/free-regular-svg-icons';
import type { UserPublicProfile } from '@user/PublicProfile';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { useCurrentUserId, useUserType } from '@framework/auth';
import { useTranslation } from 'react-i18next';

type Props = {
  message?: string;
  linkTo?: string;
  linkToMessage?: string;
};

export const NotFound: React.FC<Props> = ({ message, linkTo, linkToMessage }: Props) => {
  const currentUserId = useCurrentUserId();
  const currentUserProfile = useCurrentUserPublicProfile();
  const isAnonymousUser = useUserType() === 'anonymous';
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center" style={{ height: `calc(100vh - 5rem)` }}>
      <div className="flex flex-col items-center rounded-lg bg-white px-5 py-2">
        <FontAwesomeIcon icon={faFrown} className="text-4xl text-brand" />

        <div className="mt-2 text-center text-2xl font-bold text-brand">
          {message || t('framework.notFound.message')}
        </div>

        <div className="mt-4 text-center text-2xl font-bold">
          <a href={linkTo || '/'}>
            <Button>{linkToMessage || t('framework.notFound.backToTop')}</Button>
          </a>
        </div>

        <div className="pt-8" />

        <NotFoundContent
          isAuthenticated={currentUserId !== null}
          isAnonymousUser={isAnonymousUser}
          currentUserProfile={currentUserProfile}
        />
      </div>
    </div>
  );
};

type ContentProps = {
  isAuthenticated: boolean;
  isAnonymousUser: boolean;
  currentUserProfile: UserPublicProfile | null;
};

/**
 * 対象ページが見つからない場合のコンテンツ
 * (新ログインフォームを利用)
 */
const NotFoundContent: React.FC<ContentProps> = ({
  isAuthenticated,
  isAnonymousUser,
  currentUserProfile,
}: ContentProps) => {
  const { t } = useTranslation();

  // ログイン済みかどうかの判定に公開プロフィールを使わない。公開プロフィールは RTDB からの
  // 読み込みが完了するまで null であり、ログイン済みのユーザーにログインフォームだけを
  // 見せてしまう(ログアウトされたように見える)ため。
  if (!isAuthenticated) {
    return <LoginForm />;
  }

  // 匿名ユーザとしてログイン状態ならば、その旨を表示して、正規のBalusユーザとしてログインを促す
  if (isAnonymousUser) {
    return (
      <>
        <div className="pb-4 text-center">
          {t('framework.notFound.anonymousLogin')}
          <br />
          {t('framework.notFound.anonymousHint')}
        </div>
        <LoginForm />
      </>
    );
  }

  // 通常ユーザとしてログイン済みでアクセスできない場合には、ユーザ名を表示して確認を促す
  return (
    <>
      <div className="pb-4 text-center">
        {currentUserProfile
          ? t('framework.notFound.loggedInAs', { name: currentUserProfile.name })
          : t('framework.notFound.loggedIn')}
        <br />
        {t('framework.notFound.switchAccountHint')}
      </div>
      <LoginForm />
    </>
  );
};
